import { useMemo } from 'react';
import { computeTsaEconomics, fmt, fmtMoney, monthLabel, type SeparationState } from '@integrate/core';
import { MonthChart } from './MonthChart.js';
import { StatTile } from '../ui/StatTile.js';

/**
 * Fee versus replacement: what the transition costs, month by month, and whether the exits are real.
 *
 * Ported from `renderTsaEconomicsPane` (orgchart.html:6732–6779). The pane is lens-free by construction —
 * it reads the active scenario's design and nothing about who is looking — which is why it takes neither
 * a perspective nor a filter set.
 *
 * The sign convention is the original's and needs saying, because two columns here use opposite ones for
 * the same fee. `€ / month` is signed from the carve-out's side, so a reverse TSA shows negative. `Δ /
 * month` is `replacement − fee` from the RECEIVING side's view, which is coherent (the receiving side both
 * pays the fee and builds the replacement) but means a reader computing Δ from the two printed cells of a
 * reverse TSA gets a different number than the one printed. The original's footnote said only "from the
 * paying side's view"; this one says which is which.
 */
export function TsaEconomics({ state }: { state: SeparationState }): JSX.Element {
  const E = useMemo(() => computeTsaEconomics(state), [state]);

  if (!E.perTsa.length) {
    return (
      <p className="muted" data-testid="econ-empty">
        No TSAs registered, so there is nothing to price. Add the services the carve-out cannot run
        stand-alone on Day 1 in the register.
      </p>
    );
  }

  const months = E.monthly.map((m) => m.m);

  return (
    <section data-testid="tsa-economics">
      <div className="stat-grid" data-testid="econ-tiles">
        <StatTile
          label="Net fees at Day 1"
          valueTestId="econ-net-day1"
          value={fmtMoney(E.netMonthAtDay1)}
          sub="per month · positive = the carve-out pays"
        />
        <StatTile
          label="Fees paid (cumulative)"
          valueTestId="econ-fees-paid"
          value={fmtMoney(E.feesPaid)}
          sub="over each agreement's term"
        />
        <StatTile
          label="Fees received (cumulative)"
          valueTestId="econ-fees-received"
          value={fmtMoney(E.feesReceived)}
          sub="reverse TSAs"
        />
        <StatTile
          label="Replacement run-rate"
          valueTestId="econ-repl-run-rate"
          value={fmtMoney(E.replRunRate)}
          sub={`p.a. loaded · carve-side · ×${E.mult}`}
        />
        {E.remainInternalization > 0 && (
          <StatTile
            label="RemainCo internalisation"
            valueTestId="econ-remain-internal"
            value={fmtMoney(E.remainInternalization)}
            sub={`p.a. loaded · ${fmt(E.remainReplN)} ${E.remainReplN === 1 ? 'hire' : 'hires'}`}
          />
        )}
        <StatTile
          label="Uncovered TSAs"
          valueTestId="econ-uncovered"
          value={fmt(E.uncovered)}
          sub={E.uncovered ? 'no filled, dated replacement in time' : 'every exit is planned'}
          danger={E.uncovered > 0}
        />
      </div>

      {/*
        Both series are € per MONTH, which is dimensionally honest and worth stating anyway, because the
        tiles above are annual run-rates and the engine divides by twelve between them.

        The two cost segments are one hue at two steps rather than two hues: they are both money the
        carve-out pays, so the split is part-to-whole, not identity. Reverse-TSA income sits below the zero
        line as the opposite pole — which the original's chart could only draw when the NET went negative,
        while its legend promised the series unconditionally and the tile above it reported €48,000 of
        income the chart never showed.
      */}
      <MonthChart
        testId="econ-chart"
        caption="TSA fees and replacement cost"
        unit="€ per month, months after Day 1"
        months={months}
        labelOf={(m) => monthLabel(m, state.assumptions)}
        scaleStep={E.horizon > 18 ? 6 : 3}
        format={fmtMoney}
        ariaLabel={
          `Monthly TSA cost over ${E.horizon} months after Day 1. Fees paid start at ` +
          `${fmtMoney(E.monthly[0]?.feesPaid ?? 0)} per month and fall as each agreement ends; ` +
          `replacement run-rate ramps in to ${fmtMoney(E.monthly.at(-1)?.repl ?? 0)} per month. ` +
          `Reverse-TSA income is drawn below the zero line.`
        }
        series={[
          {
            key: 'feesPaid',
            label: 'Fees paid',
            side: 'up',
            color: 'accent',
            values: E.monthly.map((m) => m.feesPaid),
          },
          {
            key: 'repl',
            label: 'Replacement run-rate',
            side: 'up',
            color: 'accent2',
            values: E.monthly.map((m) => m.repl),
          },
          {
            key: 'feesReceived',
            label: 'Fees received (reverse TSA)',
            side: 'down',
            color: 'success',
            values: E.monthly.map((m) => m.feesReceived),
          },
        ]}
        note={
          E.replUndatedN > 0
            ? `${fmt(E.replUndatedN)} linked ${E.replUndatedN === 1 ? 'replacement carries' : 'replacements carry'} no needed-by month, so ${E.replUndatedN === 1 ? 'it is' : 'they are'} not in the ramp — give ${E.replUndatedN === 1 ? 'it' : 'them'} a month on the person's panel and the curve will show ${E.replUndatedN === 1 ? 'it' : 'them'}. The run-rate tile above counts ${E.replUndatedN === 1 ? 'it' : 'them'} regardless: the position exists, it just has no date.`
            : undefined
        }
      />

      <div className="table-scroll" style={{ marginTop: 14 }}>
        <table className="tsa-table" data-testid="econ-table">
          <thead>
            <tr>
              <th>TSA</th>
              <th>Direction</th>
              <th className="num">€ / month</th>
              <th className="num">Months</th>
              <th className="num">Cum. fees</th>
              <th className="num">Repl.</th>
              <th className="num">Repl. € / mo</th>
              <th className="num">Δ / month</th>
              <th className="num">Ready</th>
              <th className="num">Exit gap</th>
            </tr>
          </thead>
          <tbody>
            {E.perTsa.map((x) => (
              <tr key={x.tsa.id} data-testid={`econ-row-${x.tsa.id}`}>
                <td>
                  {x.tsa.name}
                  {x.tsa.status === 'exited' && (
                    <span className="pill" title="Recorded as over, so it bills nothing from Day 1 forward">
                      exited
                    </span>
                  )}
                </td>
                <td>{x.tsa.direction === 'provided' ? '⇠ Provided' : '⇢ Received'}</td>
                <td className="num">{fmtMoney(x.fee * x.sign)}</td>
                <td className="num">{x.termAgreed ? x.dur : 'no term'}</td>
                <td className="num">{fmtMoney(x.cumFees)}</td>
                {/*
                  Linked and filled are two numbers, and the original printed only the first — so a TSA
                  whose sole replacement was an open requisition read as matched.
                */}
                <td
                  className="num"
                  data-testid={`econ-repl-${x.tsa.id}`}
                  data-linked={x.repl.length}
                  data-filled={x.replFilled.length}
                  title={x.repl.map((b) => b.name).join(', ') || 'Nothing linked yet'}
                >
                  {x.repl.length === 0
                    ? '—'
                    : x.replFilled.length === x.repl.length
                      ? `${fmt(x.repl.length)} ◆`
                      : `${fmt(x.replFilled.length)}/${fmt(x.repl.length)} filled`}
                </td>
                <td className="num">{x.replFilled.length ? fmtMoney(x.replMonthly) : '—'}</td>
                <td className={`num ${deltaClass(x.monthlyDelta)}`}>
                  {x.monthlyDelta == null
                    ? x.repl.length
                      ? 'not priced'
                      : '—'
                    : `${x.monthlyDelta > 0 ? '+' : ''}${fmtMoney(x.monthlyDelta)}`}
                </td>
                <td className="num" data-testid={`econ-ready-${x.tsa.id}`}>
                  {x.readyMonth == null ? (
                    <span className="warnish" title="No filled replacement carries a needed-by month">
                      {x.replUndatedN ? 'not dated' : '—'}
                    </span>
                  ) : (
                    `M${x.readyMonth}`
                  )}
                </td>
                <td className={`num ${x.exitGap != null && x.exitGap > 0 ? 'bad' : ''}`}>
                  {x.exitGap == null
                    ? '—'
                    : `${x.exitGap > 0 ? '+' : ''}${fmt(x.exitGap)} mo`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="muted">
        <strong>€ / month</strong> is signed from the carve-out&rsquo;s side, so a reverse TSA is negative.{' '}
        <strong>Δ / month</strong> is the replacement run-rate less the fee, from the{' '}
        <em>receiving</em> side&rsquo;s view — the side that both pays the fee and builds the replacement
        — so for a reverse TSA it is not the difference of the two cells printed beside it.{' '}
        <strong>Exit gap</strong> is when the last filled replacement is due, less when the service ends:
        positive means the exit misses. A blank means the gap cannot be stated, not that it is zero.
      </p>
    </section>
  );
}

function deltaClass(delta: number | null): string {
  if (delta == null) return '';
  return delta > 0 ? 'bad' : 'good';
}
